import styles from './common.module.scss';

import cn from 'classnames';
import { ReactComponent as SolanaIcon } from '@assets/svg/solana.svg';
import { ReactComponent as CoinIcon } from '@assets/svg/coin.svg';
import { ReactComponent as SolanaNoBgIcon } from '@assets/svg/solana-no-background.svg';
import { ChangeEvent, useEffect, useState } from 'react';
import Input from './Input';
import Button from '@components/button';
import useTimer from '@hooks/useTimer';

import readyAudio from '@assets/audio/ready.mp3';
import errorAudio from '@assets/audio/error.mp3';
import useSound from 'use-sound';

import {
    convertDuelanaPointsToSOL,
    toastErrorMessage,
    toastSuccessMessage,
} from '@utils/utils';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import useReduxState from '@hooks/useReduxState';
import { useMutation } from '@apollo/client';
import { ADD_USER_WITHDRAWAL } from '@graphql/Mutations';

interface ICalculatorState {
    duelpoints: number;
    solana: number;
}

const Withdraw = (): JSX.Element => {
    const [{ userData: user, userTransactions }, setTransactions] =
        useReduxState((state) => state);
    const [playReadySound] = useSound(readyAudio, { interrupt: true });
    const [playErrorSound] = useSound(errorAudio, { interrupt: true });

    const { withdraws } = userTransactions;

    const withdrawCoolDown =
        withdraws.length > 0
            ? Date.now() - withdraws[withdraws.length - 1].timestamp * 1000
            : 0;

    const { seconds, minutes } = useTimer(
        withdrawCoolDown <= 60 * 1000 * 2
            ? 60 * 1000 * 2 - withdrawCoolDown
            : 0,
    );

    const [isWithdrawing, setIsWithdrawing] = useState<boolean>(false);
    const [{ duelpoints, solana }, setCalculator] = useState<ICalculatorState>({
        duelpoints: 0,
        solana: 0,
    });

    const [addWithdraw] = useMutation(ADD_USER_WITHDRAWAL, {
        onCompleted: (data) => {
            const { points_equivalent } = data.addUserWithdrawal;

            setTransactions({
                type: 'USER_UPDATE_BALANCE',
                arg: { balance: points_equivalent, type: 'substract' },
            });

            setTransactions({
                type: 'ADD_USER_WIDTHRAWAL',
                arg: {
                    transaction: data.addUserWithdrawal,
                },
            });

            toastSuccessMessage(`Succesfully withdrew ${duelpoints} Chips`);
            setIsWithdrawing(false);

            playReadySound();
        },
        onError: (error) => {
            setIsWithdrawing(false);
            toastErrorMessage(error.message);
            playErrorSound();
        },
    });

    useEffect(() => {
        const sol =
            duelpoints > 0
                ? +(
                      (convertDuelanaPointsToSOL(duelpoints) /
                          LAMPORTS_PER_SOL) *
                      0.85
                  ).toFixed(5)
                : 0;
        setCalculator((prevState) => ({ ...prevState, solana: sol }));
    }, [duelpoints]);

    const handleWithdraw = async (): Promise<void> => {
        if (!user || !duelpoints) return;
        if (isWithdrawing)
            return toastErrorMessage('You are already withdrawing an amount');
        setIsWithdrawing(true);
        await addWithdraw({
            variables: {
                userId: user.id,
                ammount: duelpoints,
            },
        });
    };

    const onChange = (e: ChangeEvent<HTMLInputElement>): void =>
        setCalculator((formState) => ({
            ...formState,
            duelpoints: Number(e.target.value),
        }));

    /* ClassNames */

    const inputsClasses = cn(styles.body__inputs, styles.deposit);

    return (
        <div className={styles.transference}>
            <section className={styles.head}>
                <div>
                    <h3>
                        <SolanaIcon />
                        Withdraw with <span>Solana</span>
                    </h3>
                    <p>1 SOL = $154.99 USD</p>
                </div>
                <Button
                    color="dark-lighten"
                    radius="normal"
                    className={styles.head__history}
                >
                    History
                </Button>
            </section>

            <section className={styles.body}>
                <h4>Value Calculator*</h4>
                <div className={inputsClasses}>
                    <Input
                        type="number"
                        value={duelpoints === 0 ? '' : duelpoints.toString()}
                        onChange={onChange}
                        icon={<CoinIcon />}
                        name="duelpoints"
                        placeholder="Enter Coins Amount"
                    />
                    <p className={styles.body__equal}>=</p>
                    <Input
                        type="number"
                        value={solana === 0 ? '' : solana.toString()}
                        readOnly
                        icon={<SolanaNoBgIcon />}
                        name="solana"
                        placeholder="Enter Solana Amount"
                    />
                </div>
                <p className={styles.body__disclaimer}>
                    *The value of Solana may vary between now and the time we
                    receive your payment
                </p>
            </section>

            <Button
                color="green"
                size="normal"
                radius="pill"
                className={styles.submit}
                onClick={handleWithdraw}
            >
                Withdraw
                {withdrawCoolDown <= 60 * 1000 * 2 && (
                    <div className={styles.counter}>
                        {minutes}:{seconds}
                    </div>
                )}
            </Button>
            <section className={styles.footer}>
                <Button color="dark-lighten" radius="normal">
                    History
                </Button>
                <Button color="dark-lighten" radius="normal">
                    Coupon
                </Button>
            </section>
        </div>
    );
};
export default Withdraw;
